import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    Avatar,
    Button,
    Tabs,
    Tab,
    Link as MuiLink,
    Grid,
    Divider,
    useTheme
} from '@mui/material';
import {
    PhoneIcon,
    EmailIcon,
    LanguageIcon,
    LocationOnIcon,
    SchoolIcon,
    WorkIcon,
    FacebookIcon,
    TwitterIcon,
    LinkedInIcon,
    InstagramIcon,
    MessageIcon,
    QrCodeIcon,
    OpenInNewIcon,
    WebsiteIcon,
    LocationIcon
} from 'icons/materialIcons';
import getIconForType from 'utils/icons/getIconForType';
import placeholderImage from 'images/placeholder-grey-image.jpg';
import avatarPlaceholder from 'images/avatar-cropped.png';
import TeamProductsServicesPreviewCardDesign5 from 'team/components/editMember/links/productsServices/ProductsServicesPreview/TeamProductsServicesPreviewCardDesign5';
import TeamQRCodeModal from 'team/shared/features/card/TeamQRCodeModal';
import { useEventTracking } from 'services/eventTrackingService';
import TeamLeadCaptureOverlay from 'team/components/editMember/leadCapture/TeamLeadCaptureOverlay';
import { getContactLinkCategory } from 'utils/getContactLinkCategory';
import { formatPhoneNumber, isPhoneContactType } from 'utils/phoneUtils';

const TeamCardDesign5 = ({
    cardId,
    cardFullName,
    cardJobTitle,
    cardCompany,
    cardTheme,
    cardProfilePictureUrl,
    cardCoverPhotoUrl,
    cardCompanyLogoUrl,
    contextCardLayout,
    cardLinkColor,
    cardLocation,
    cardBio,
    cardPronoun,
    formattedPronoun,
    visibleContactLinks,
    previewContactLink,
    handleSaveContact,
    customLinkTypeLabels = {},
    showLeadCaptureAuto = false,
    cardFont = 'Inter',
}) => {
    // Access the theme
    const theme = useTheme();

    // Initialize tracking service
    const tracking = useEventTracking();

    // State for active tab
    const [activeTab, setActiveTab] = useState(0);

    // State for QR modal
    const [qrModalOpen, setQrModalOpen] = useState(false);

    // State for Lead Capture modal - separate states for different triggers
    const [showLeadCaptureButton, setShowLeadCaptureButton] = useState(false);

    // State for scrollable tabs detection
    const [useScrollableTabs, setUseScrollableTabs] = useState(false);

    // Get theme styles and colors EARLY
    const styles = theme.card.cardDesign5;
    const colors = styles.colors;
    const values = styles.values;
    const cssProps = styles.cssProperties;
    const fallbackText = styles.fallbackText;

    // Font style to apply to all text elements
    const fontStyle = {
        fontFamily: `"${cardFont}", sans-serif`
    };
    
    // Helper function to merge font style with existing styles
    const applyFont = (existingStyles) => ({
        ...existingStyles,
        ...fontStyle
    });

    // Primary color - using cardTheme or default from theme
    const primaryColor = cardTheme || colors.primary;
    // Use cardLinkColor as secondary color, fallback to theme default
    const secondaryColor = cardLinkColor || colors.secondary;
    const profileCardColor = cardLinkColor || colors.profileCard;

    // MEMOIZE the link processing functions to prevent unnecessary re-calculations
    const socialLinks = useMemo(() => {
        const links = visibleContactLinks.filter(link => getContactLinkCategory(link.type) === 'Social Media');
        // console.log('🔍 TeamCardDesign5: Social links:', links.map(link => ({ type: link.type, category: getContactLinkCategory(link.type) })));
        return links;
    }, [visibleContactLinks]);

    const productLinks = useMemo(() => {
        // console.log('🔍 GET_PRODUCT_LINKS - Input:', {
        //     visibleContactLinks,
        //     visibleContactLinksLength: visibleContactLinks?.length || 0
        // });

        const links = visibleContactLinks.filter(link => {
            const category = getContactLinkCategory(link.type);
            const isProductLink = category === 'Products & Services';
            // console.log('🔍 GET_PRODUCT_LINKS - Filtering:', {
            //     linkType: link.type,
            //     category,
            //     isProductLink
            // });
            return isProductLink;
        });

        // console.log('🔍 TeamCardDesign5: Product links:', links.map(link => ({ type: link.type, category: getContactLinkCategory(link.type) })));
        return links;
    }, [visibleContactLinks]);

    const contactLinks = useMemo(() => {
        const links = visibleContactLinks.filter(link => {
            const category = getContactLinkCategory(link.type);
            return category !== 'Social Media' && category !== 'Products & Services';
        });
        // console.log('🔍 TeamCardDesign5: Contact links:', links.map(link => ({ type: link.type, category: getContactLinkCategory(link.type) })));
        return links;
    }, [visibleContactLinks]);

    // Function to get custom label with fallback
    const getCustomLabel = useCallback((linkType) => {
        if (customLinkTypeLabels[linkType] !== undefined) {
            return customLinkTypeLabels[linkType];
        }

        switch (linkType) {
            case 'Social':
                return customLinkTypeLabels['Social'] !== undefined ? customLinkTypeLabels['Social'] : 'Social';
            case 'Products & Services':
                return customLinkTypeLabels['Products & Services'] !== undefined ? customLinkTypeLabels['Products & Services'] : 'Products & Services';
            case 'Contact':
            default:
                return customLinkTypeLabels['Contact'] !== undefined ? customLinkTypeLabels['Contact'] : 'Contact';
        }
    }, [customLinkTypeLabels]);

    // MEMOIZE the hasTabContent function
    const hasTabContent = useCallback((tabType) => {
        switch (tabType) {
            case 'contact':
                return contactLinks.length > 0 || cardJobTitle || cardCompany || cardLocation;
            case 'social':
                return socialLinks.length > 0;
            case 'products':
                return productLinks.length > 0;
            default:
                return false;
        }
    }, [contactLinks.length, socialLinks.length, productLinks.length, cardJobTitle, cardCompany, cardLocation]);

    // MEMOIZE the getAvailableTabs function
    const availableTabs = useMemo(() => {
        const tabs = [];

        if (hasTabContent('contact')) {
            tabs.push({
                type: 'contact',
                label: getCustomLabel('Contact'),
                index: tabs.length
            });
        }

        if (hasTabContent('social')) {
            tabs.push({
                type: 'social',
                label: getCustomLabel('Social'),
                index: tabs.length
            });
        }

        if (hasTabContent('products')) {
            tabs.push({
                type: 'products',
                label: getCustomLabel('Products & Services'),
                index: tabs.length
            });
        }

        return tabs;
    }, [hasTabContent, getCustomLabel]);

    // Helper function to get darker shade of a color using theme values
    const getDarkerShade = useCallback((color, amount = values.darkenAmount) => {
        const hex = color.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);

        const newR = Math.round(r * (1 - amount));
        const newG = Math.round(g * (1 - amount));
        const newB = Math.round(b * (1 - amount));

        return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
    }, [values.darkenAmount]);

    // Get appropriate hover color for Save Contact button using theme values
    const getSaveButtonHoverColor = useCallback(() => {
        if (secondaryColor && secondaryColor !== primaryColor) {
            return secondaryColor;
        }
        return getDarkerShade(primaryColor, values.lightenAmount);
    }, [secondaryColor, primaryColor, getDarkerShade, values.lightenAmount]);

    // Helper function to handle link clicks with tracking
    const handleLinkClick = useCallback((link, linkType) => {
        // First, handle the link opening
        if (link?.details?.formattedLink) {
            window.open(link.details.formattedLink, '_blank');
        } else if (link?.details?.linkUrl) {
            if (linkType === 'product' || linkType === 'service') {
                const url = link.details.linkUrl.startsWith('http')
                    ? link.details.linkUrl
                    : `https://${link.details.linkUrl}`;
                window.open(url, '_blank');
            } else {
                const url = link.details.linkUrl.startsWith('http') ?
                    link.details.linkUrl :
                    `https://${link.details.linkUrl}`;
                window.open(url, '_blank');
            }
        }

        // Then attempt tracking with error handling
        if (tracking && link?.details && cardId) {
            try {
                const trackingData = {
                    linkType: linkType || 'unknown',
                    linkUrl: link.details.linkUrl || '',
                    linkTitle: link.details.linkTitle || linkType || 'untitled',
                    cardId: cardId
                };

                if (trackingData.cardId && trackingData.linkType) {
                    tracking.trackLinkTap(trackingData);
                }
            } catch (error) {
                console.warn('Link tap tracking failed:', error);
            }
        }
    }, [tracking, cardId]);

    // Helper function to get icon for link type using theme styles
    const getLinkIcon = useCallback((linkType) => {
        const type = (linkType || '').toLowerCase();
        switch (type) {
            case 'email':
                return <EmailIcon sx={styles.iconStyles} />;
            case 'phone':
            case 'call':
                return <PhoneIcon sx={styles.iconStyles} />;
            case 'website':
                return <WebsiteIcon sx={styles.iconStyles} />;
            case 'address':
                return <LocationIcon sx={styles.iconStyles} />;
            default:
                return React.cloneElement(getIconForType(linkType), {
                    sx: styles.iconStyles
                });
        }
    }, [styles.iconStyles]);

    // Helper function to get social icon using theme colors
    const getSocialIcon = useCallback((linkType) => {
        const type = (linkType || '').toLowerCase();
        switch (type) {
            case 'facebook':
                return { icon: <FacebookIcon sx={styles.socialIconStyles} />, color: colors.facebook };
            case 'twitter':
            case 'x':
                return { icon: <TwitterIcon sx={styles.socialIconStyles} />, color: colors.twitter };
            case 'linkedin':
                return { icon: <LinkedInIcon sx={styles.socialIconStyles} />, color: colors.linkedin };
            case 'instagram':
                return { icon: <InstagramIcon sx={styles.socialIconStyles} />, color: colors.instagram };
            case 'wechat':
                return { icon: <MessageIcon sx={styles.socialIconStyles} />, color: colors.wechat };
            default:
                return {
                    icon: React.cloneElement(getIconForType(linkType), {
                        sx: styles.socialIconStyles
                    }),
                    color: secondaryColor || colors.secondary
                };
        }
    }, [styles.socialIconStyles, colors, secondaryColor]);

    // Helper function to format link label
    const formatLinkLabel = useCallback((link) => {
        if (link?.details?.linkTitle) {
            return link.details.linkTitle.toUpperCase();
        }

        const type = (link?.type || '');
        switch (type.toLowerCase()) {
            case 'call':
                return fallbackText.phoneLabel;
            case 'x':
                return fallbackText.twitterLabel;
            case 'address':
                return fallbackText.addressLabel;
            default:
                return type.toUpperCase();
        }
    }, [fallbackText]);

    // Helper function to check if an address has a valid display value
    const hasValidAddressDisplay = useCallback((link) => {
        return link.details && (
            link.details.workAddress ||
            link.details.linkUrl ||
            link.details.coordinates
        );
    }, []);

    // Helper function to get the map source URL for addresses
    const getMapSource = useCallback((link) => {
        if (!link.details) return null;

        if (link.details.coordinates) {
            return `https://maps.googleapis.com/maps/api/staticmap?center=${link.details.coordinates.lat},${link.details.coordinates.lng}&zoom=15&size=600x300&maptype=roadmap&markers=color:red%7C${link.details.coordinates.lat},${link.details.coordinates.lng}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;
        }

        const address = link.details.workAddress || link.details.linkUrl;
        if (address) {
            return `https://maps.googleapis.com/maps/api/staticmap?center=${encodeURIComponent(address)}&zoom=15&size=600x300&maptype=roadmap&markers=color:red%7C${encodeURIComponent(address)}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;
        }

        return null;
    }, []);

    // Helper function to determine if contact type should use address styling
    const isAddressType = useCallback((link) => {
        return link.type?.toLowerCase() === 'address';
    }, []);

    // Render helper functions
    const renderContactLinkItem = useCallback((link) => {
        const hasValidData = link?.details?.linkTitle || link?.details?.linkUrl;

        if (!hasValidData) {
            return null;
        }

        const isAddress = isAddressType(link);

        return (
            <Box key={link._id}>
                <Box
                    sx={isAddress ? styles.contactItemAddress : styles.contactItem}
                    onClick={() => handleLinkClick(link, link.type)}
                >
                    <Box sx={{
                        ...(isAddress ? styles.contactIconAddress : styles.contactIcon),
                        bgcolor: secondaryColor
                    }}>
                        {getLinkIcon(link.type)}
                    </Box>
                    <Box sx={{ width: '100%', minWidth: 0 }}>
                        <Typography variant="caption" sx={applyFont(styles.contactLabel)}>
                            {formatLinkLabel(link)}
                        </Typography>
                        <Typography variant="body2" sx={applyFont(styles.contactValue)}>
                            {(() => {
                                if (isPhoneContactType(link.type) && link.details?.linkUrl) {
                                    return formatPhoneNumber(link.details.linkUrl, 'SG');
                                }
                                if (isAddress && link.details?.linkTitle && link.details.linkTitle !== fallbackText.addressLabel) {
                                    return link.details.linkUrl;
                                }
                                return link.details?.linkUrl || link.type;
                            })()}
                        </Typography>
                    </Box>
                </Box>
                {isAddress && hasValidAddressDisplay(link) && (
                    <Box
                        sx={{
                            ...styles.map.container,
                            mt: 1,
                            mb: 2,
                            backgroundColor: colors.greyBackground,
                            margin: { xs: "0.25rem 1.5rem", sm: "0.375rem 2rem", md: "0.5rem 2.5rem" },
                            borderRadius: "8px",
                            overflow: "hidden",
                            cursor: 'pointer',
                            position: 'relative'
                        }}
                        onClick={() => handleLinkClick(link, link.type)}
                    >
                        {(() => {
                            const mapSource = getMapSource(link);
                            if (mapSource) {
                                return (
                                    <>
                                        <img
                                            src={mapSource}
                                            alt="Location map"
                                            style={styles.map.image}
                                            onError={(e) => {
                                                const address = link.details.workAddress || link.details.linkUrl;
                                                if (address && !e.target.dataset.fallbackAttempted) {
                                                    e.target.dataset.fallbackAttempted = 'true';
                                                    e.target.src = `https://maps.googleapis.com/maps/api/staticmap?center=${encodeURIComponent(address)}&zoom=13&size=600x300&maptype=roadmap&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;
                                                }
                                            }}
                                        />
                                        <Box sx={styles.map.overlay}>
                                            <Typography variant="body2" sx={applyFont({ color: 'white', fontWeight: 500 })}>
                                                View larger map
                                            </Typography>
                                        </Box>
                                    </>
                                );
                            } else {
                                return (
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: colors.greyLight,
                                            color: colors.greyText
                                        }}
                                    >
                                        <Typography variant="body2" sx={fontStyle}>
                                            📍 {link.details.workAddress || link.details.linkUrl}
                                        </Typography>
                                    </Box>
                                );
                            }
                        })()}
                    </Box>
                )}
            </Box>
        );
    }, [styles, colors, secondaryColor, fallbackText, isAddressType, handleLinkClick, getLinkIcon, formatLinkLabel, hasValidAddressDisplay, getMapSource, applyFont, cardFont]);

    const renderSocialLinkItem = useCallback((link) => {
        const hasValidData = link?.details?.linkTitle || link?.details?.linkUrl;

        if (!hasValidData) {
            return null;
        }

        const { icon, color } = getSocialIcon(link.type);
        return (
            <Box
                key={link._id}
                sx={styles.contactItem}
                onClick={() => handleLinkClick(link, link.type)}
            >
                <Box sx={{
                    ...styles.contactIconLarge,
                    bgcolor: color
                }}>
                    {link.details?.icon ? (
                        <Box
                            sx={{
                                ...styles.socialIconImage,
                                backgroundImage: `url(${link.details.icon})`
                            }}
                        />
                    ) : icon}
                </Box>
                <Box sx={{ minWidth: 0, width: '100%' }}>
                    <Typography variant="caption" sx={applyFont(styles.contactLabel)}>
                        {formatLinkLabel(link)}
                    </Typography>
                    <Typography variant="body2" sx={applyFont(styles.contactValue)}>
                        {(() => {
                            if (isPhoneContactType(link.type) && link.details?.linkUrl) {
                                return formatPhoneNumber(link.details.linkUrl, 'SG');
                            }
                            return link.details?.linkTitle || link.details?.linkUrl || link.type;
                        })()}
                    </Typography>
                </Box>
            </Box>
        );
    }, [styles, getSocialIcon, handleLinkClick, formatLinkLabel, applyFont, cardFont]);

    // Main render functions
    const renderContactTab = useCallback(() => {
        return (
            <Box sx={styles.contactTabContainer}>
                {(cardJobTitle || cardCompany) && (
                    <>
                        <Typography variant="subtitle1" sx={applyFont(styles.sectionTitle)}>
                            ABOUT
                        </Typography>

                        {cardJobTitle && (
                            <Box sx={styles.contactItem}>
                                <Box sx={{
                                    ...styles.contactIcon,
                                    bgcolor: secondaryColor
                                }}>
                                    <WorkIcon sx={styles.iconStyles} />
                                </Box>
                                <Box sx={{ minWidth: 0, width: '100%' }}>
                                    <Typography variant="caption" sx={applyFont(styles.contactLabel)}>
                                        {fallbackText.jobTitleLabel}
                                    </Typography>
                                    <Typography variant="body2" sx={applyFont(styles.contactValue)}>
                                        {cardJobTitle}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {cardCompany && (
                            <Box sx={styles.contactItem}>
                                <Box sx={{
                                    ...styles.contactIcon,
                                    bgcolor: secondaryColor
                                }}>
                                    <WorkIcon sx={styles.iconStyles} />
                                </Box>
                                <Box sx={{ minWidth: 0, width: '100%' }}>
                                    <Typography variant="caption" sx={applyFont(styles.contactLabel)}>
                                        {fallbackText.companyNameLabel}
                                    </Typography>
                                    <Typography variant="body2" sx={applyFont(styles.contactValue)}>
                                        {cardCompany}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </>
                )}

                {contactLinks.length > 0 && (
                    <>
                        <Typography variant="subtitle1" sx={styles.sectionTitle}>
                            {fallbackText.contactSectionTitle}
                        </Typography>
                        {contactLinks.map((link, index) => (
                            <Box key={link.linkId || link.uuid || index}>
                                {renderContactLinkItem(link)}
                            </Box>
                        ))}
                    </>
                )}

                {cardLocation && !contactLinks.find(link => link.type?.toLowerCase() === 'address') && (
                    <Box sx={styles.contactItem}>
                        <Box sx={{
                            ...styles.contactIcon,
                            bgcolor: secondaryColor
                        }}>
                            <LocationIcon sx={styles.iconStyles} />
                        </Box>
                        <Box sx={{ minWidth: 0, width: '100%' }}>
                            <Typography variant="caption" sx={applyFont(styles.contactLabel)}>
                                {fallbackText.addressLabel}
                            </Typography>
                            <Typography variant="body2" sx={applyFont(styles.contactValue)}>
                                {cardLocation}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {!cardJobTitle && !cardCompany && contactLinks.length === 0 && !cardLocation && (
                    <Box sx={styles.emptyMessage}>
                        <Typography variant="body2" sx={applyFont(styles.emptyMessage)}>
                            No contact information available.
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    }, [contactLinks, cardJobTitle, cardCompany, cardLocation, styles, secondaryColor, fallbackText, renderContactLinkItem]);

    const renderSocialTab = useCallback(() => {
        return (
            <Box sx={styles.contactTabContainer}>
                {socialLinks.map((link, index) => (
                    <Box key={link.linkId || link.uuid || index}>
                        {renderSocialLinkItem(link)}
                    </Box>
                ))}

                {socialLinks.length === 0 && (
                    <Box sx={styles.emptyMessage}>
                        <Typography variant="body2" sx={applyFont(styles.emptyMessage)}>
                            {fallbackText.noSocialLinks}
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    }, [socialLinks, styles, fallbackText, renderSocialLinkItem]);

    const renderProductsTab = useCallback(() => {
        return (
            <Box sx={styles.productSection}>
                {productLinks && productLinks.length > 0 ? (
                    <Box sx={styles.productSectionContent}>
                        {productLinks.map((productLink, index) => (
                            <Box key={productLink._id || index} sx={styles.productItem}>
                                <TeamProductsServicesPreviewCardDesign5
                                    link={productLink}
                                    cardLinkColor={primaryColor}
                                    cardFont={cardFont}
                                    isFullDisplay={false}
                                />
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Box sx={styles.emptyMessage}>
                        <Typography variant="body2" sx={applyFont(styles.emptyMessage)}>
                            {fallbackText.noProducts}
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    }, [productLinks, styles, fallbackText, primaryColor]);

    // NOW the tabContent useMemo can safely reference these functions
    const tabContent = useMemo(() => {
        const tab = availableTabs[activeTab];
        if (!tab) return null;

        switch (tab.type) {
            case 'contact':
                return renderContactTab();
            case 'social':
                return renderSocialTab();
            case 'products':
                return renderProductsTab();
            default:
                return null;
        }
    }, [activeTab, availableTabs, renderContactTab, renderSocialTab, renderProductsTab]);

    // Handle automatic lead capture display
    useEffect(() => {
        // This would be controlled by the parent component
    }, [showLeadCaptureAuto]);

    // Debug logging useEffect - CLEANED UP
    useEffect(() => {
        // console.log('TeamCardDesign5 - visibleContactLinks length:', visibleContactLinks?.length || 0);
    }, [visibleContactLinks?.length, JSON.stringify(visibleContactLinks?.map(link => link.id))]);

    // Debug theme structure - only runs once on mount - CLEANED UP
    useEffect(() => {
        // console.log('Theme structure (debug):', {
        //     theme,
        //     card: theme.card,
        //     cardDesign5: theme.card.cardDesign5,
        //     styles,
        //     map: styles.map,
        //     overlay: styles.map?.overlay
        // });
    }, []); // Empty dependency array = only runs once on mount

    // ✅ FIXED: Scrollable tabs detection useEffect - STABLE DEPENDENCIES
    useEffect(() => {
        const needsScrollable = availableTabs.length > 3 ||
            availableTabs.some(tab => tab.label.length > 12);

        setUseScrollableTabs(needsScrollable);

        // console.log('Tab analysis:', {
        //     tabCount: availableTabs.length,
        //     longLabels: availableTabs.filter(tab => tab.label.length > 12),
        //     useScrollable: needsScrollable
        // });
    }, [
        availableTabs.length,
        JSON.stringify(availableTabs.map(tab => tab.label))
    ]); // ✅ STABLE: Uses string serialization instead of spread operator

    // ActiveTab reset useEffect
    useEffect(() => {
        if (availableTabs.length > 0 && activeTab >= availableTabs.length) {
            setActiveTab(0);
        }
    }, [availableTabs.length, activeTab]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    // Helper function to check if we should show tabs
    const shouldShowTabs = () => {
        return true;
    };

    // Handle Connect button click
    const handleConnect = () => {
        setShowLeadCaptureButton(true);
    };

    // Handle lead capture close for button-triggered modal
    const handleLeadCaptureButtonClose = () => {
        setShowLeadCaptureButton(false);
    };

    // Handle lead capture success for button-triggered modal
    const handleLeadCaptureButtonSuccess = () => {
        setShowLeadCaptureButton(false);
    };

    return (
        <Box sx={styles.mainContainer}>
            {/* Header Background */}
            <Box
                sx={{
                    ...styles.headerBackground,
                    bgcolor: (cardCoverPhotoUrl && cardCoverPhotoUrl !== placeholderImage) ? cssProps.transparent : primaryColor,
                    backgroundImage: (cardCoverPhotoUrl && cardCoverPhotoUrl !== placeholderImage) ? `url(${cardCoverPhotoUrl})` : cssProps.none,
                    backgroundSize: cssProps.backgroundSize,
                    backgroundPosition: cssProps.backgroundPosition,
                    backgroundRepeat: cssProps.backgroundRepeat,
                }}
            >
                {/* Header Content */}
                <Box sx={styles.headerContent}>
                    {/* Left side: Company Logo, Initial, or Invisible Spacer */}
                    {(cardCompanyLogoUrl || cardCompany) ? (
                        cardCompanyLogoUrl ? (
                            <Box sx={styles.companyLogo}>
                                <img
                                    src={cardCompanyLogoUrl}
                                    alt={fallbackText.companyLogoAlt}
                                    style={styles.avatarImageStyles}
                                    onError={(e) => {
                                        console.error('[TeamCardDesign5] ❌ Company logo image failed to load:', {
                                            src: cardCompanyLogoUrl,
                                            error: e,
                                            isBlob: cardCompanyLogoUrl?.startsWith('blob:')
                                        });
                                    }}
                                    onLoad={() => {
                                        console.log('[TeamCardDesign5] ✅ Company logo image loaded successfully:', cardCompanyLogoUrl);
                                    }}
                                />
                            </Box>
                        ) : (
                            <Typography
                                component="span"
                                sx={styles.companyInitial}
                            >
                                {cardCompany.charAt(0).toUpperCase()}
                            </Typography>
                        )
                    ) : (
                        <Box sx={{ width: { xs: "2.5rem", sm: "2.8125rem", md: "3.125rem" } }} />
                    )}

                    {/* QR Code Icon - Always on the right */}
                    <Box
                        onClick={() => setQrModalOpen(true)}
                        sx={{
                            ...styles.qrIcon,
                            ...styles.qrIconHover
                        }}
                    >
                        <QrCodeIcon sx={styles.qrIconInner} />
                    </Box>
                </Box>
            </Box>

            {/* White Background Section */}
            <Box sx={styles.whiteBackground}>
                {/* Profile Card Rectangle */}
                <Box sx={styles.profileCard}>
                    {/* Left Side - Profile Photo as Background */}
                    <Box
                        sx={{
                            ...styles.profilePhotoSection,
                            backgroundImage: `url(${cardProfilePictureUrl || avatarPlaceholder})`
                        }}
                    >
                        {(!cardProfilePictureUrl && !avatarPlaceholder) && (
                            <Avatar
                                src={avatarPlaceholder}
                                sx={styles.fallbackAvatar}
                            />
                        )}
                    </Box>

                    {/* Right Side - Profile Card Color Background with Name and Title */}
                    <Box
                        sx={{
                            ...styles.profileCardRightSection,
                            bgcolor: primaryColor,
                            color: colors.white
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={applyFont(styles.profileCardName)}
                        >
                            {cardFullName || fallbackText.fullName}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={applyFont({
                                ...styles.profileCardJobTitle,
                                opacity: values.profileCardOpacity
                            })}
                        >
                            {cardJobTitle}
                        </Typography>
                    </Box>
                </Box>

                {/* Secondary Bio - Consistent but reduced spacing when no bio */}
                <Box sx={{
                    paddingTop: cardBio
                        ? { xs: "2rem", sm: "2.5rem", md: "3rem" }  // Original spacing when bio exists
                        : { xs: "1.5rem", sm: "1.5rem", md: "2rem" }, // Reduced spacing when no bio
                    paddingBottom: 1,
                    textAlign: "center",
                }}>
                    {cardBio && (
                        <Typography variant="body2" sx={applyFont(styles.secondaryJobTitleText)}>
                            {cardBio}
                        </Typography>
                    )}
                </Box>

                {/* Tabs */}
                {shouldShowTabs() && (
                    <Box sx={styles.tabsContainer}>
                        <Tabs
                            value={activeTab}
                            onChange={handleTabChange}
                            variant={useScrollableTabs ? "scrollable" : "fullWidth"}
                            scrollButtons={useScrollableTabs ? "auto" : false}
                            allowScrollButtonsMobile={useScrollableTabs}
                            sx={{
                                ...styles.tabs,
                                ...(useScrollableTabs && {
                                    "& .MuiTabs-scrollButtons": {
                                        color: primaryColor,
                                        opacity: 0.7,
                                        "&.Mui-disabled": {
                                            opacity: 0.3,
                                        },
                                        "&:hover": {
                                            opacity: 1,
                                            backgroundColor: "rgba(0,0,0,0.04)",
                                        }
                                    },
                                    "& .MuiTabs-flexContainer": {
                                        gap: { xs: "0.25rem", sm: "0.5rem" },
                                        paddingX: 1,
                                    },
                                    "& .MuiTabs-scroller": {
                                        scrollBehavior: "smooth"
                                    }
                                })
                            }}
                        >
                            {availableTabs.map((tab, index) => (
                                <Tab
                                    key={tab.type}
                                    label={tab.label}
                                    sx={{
                                        ...(useScrollableTabs ? {
                                            minWidth: "auto",
                                            flexGrow: 0,
                                            flexShrink: 0,
                                            paddingX: { xs: "0.75rem", sm: "1rem", md: "1.25rem" },
                                            whiteSpace: "nowrap",
                                            textOverflow: "clip",
                                            overflow: "visible",
                                            // Override theme fontFamily with higher specificity
                                            fontFamily: `"${cardFont}", sans-serif !important`,
                                            '& .MuiTab-label': {
                                                fontFamily: `"${cardFont}", sans-serif !important`
                                            }
                                        } : {
                                            ...(tab.type === 'products' ? styles.tabItemLarge : styles.tabItem),
                                            // Override theme fontFamily with higher specificity
                                            fontFamily: `"${cardFont}", sans-serif !important`,
                                            '& .MuiTab-label': {
                                                fontFamily: `"${cardFont}", sans-serif !important`
                                            }
                                        })
                                    }}
                                />
                            ))}
                        </Tabs>
                    </Box>
                )}

                {/* Tab Content */}
                {shouldShowTabs() ? (
                    <Box sx={styles.tabContent}>
                        {tabContent}
                    </Box>
                ) : (
                    <Box sx={styles.contactTabContainer}>
                        {(cardJobTitle || cardCompany) && (
                            <>
                                <Typography variant="subtitle1" sx={styles.sectionTitle}>
                                    ABOUT
                                </Typography>

                                {cardJobTitle && (
                                    <Box sx={styles.contactItem}>
                                        <Box sx={{
                                            ...styles.contactIcon,
                                            bgcolor: secondaryColor
                                        }}>
                                            <WorkIcon sx={styles.iconStyles} />
                                        </Box>
                                        <Box sx={{ minWidth: 0, width: '100%' }}>
                                            <Typography variant="caption" sx={styles.contactLabel}>
                                                {fallbackText.jobTitleLabel}
                                            </Typography>
                                            <Typography variant="body2" sx={styles.contactValue}>
                                                {cardJobTitle}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}

                                {cardCompany && (
                                    <Box sx={styles.contactItem}>
                                        <Box sx={{
                                            ...styles.contactIcon,
                                            bgcolor: secondaryColor
                                        }}>
                                            <WorkIcon sx={styles.iconStyles} />
                                        </Box>
                                        <Box sx={{ minWidth: 0, width: '100%' }}>
                                            <Typography variant="caption" sx={styles.contactLabel}>
                                                {fallbackText.companyNameLabel}
                                            </Typography>
                                            <Typography variant="body2" sx={styles.contactValue}>
                                                {cardCompany}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            </>
                        )}
                    </Box>
                )}

                {/* Action Buttons */}
                <Box sx={styles.actionButtons}>
                    <Button
                        variant="contained"
                        onClick={handleSaveContact}
                        sx={applyFont({
                            ...styles.saveButton,
                            backgroundColor: primaryColor,
                            '&:hover': {
                                backgroundColor: getSaveButtonHoverColor()
                            }
                        })}
                    >
                        {fallbackText.saveContactButton}
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleConnect}
                        sx={applyFont({
                            ...styles.connectButton,
                            backgroundColor: secondaryColor,
                            '&:hover': {
                                backgroundColor: getSaveButtonHoverColor(),
                            },
                        })}
                    >
                        {fallbackText.connectButton}
                    </Button>
                </Box>
            </Box>

            {/* QR Code Modal */}
            <TeamQRCodeModal
                open={qrModalOpen}
                onClose={() => setQrModalOpen(false)}
                cardId={cardId}
            />

            {/* Lead Capture Overlay for Auto-popup */}
            {showLeadCaptureAuto && (
                <TeamLeadCaptureOverlay
                    onClose={() => { }}
                    onSuccess={() => { }}
                    cardLayoutType={contextCardLayout}
                    addMoreConnectButtonColor={secondaryColor}
                    triggerSource="auto"
                />
            )}

            {/* Lead Capture Overlay for Connect Button */}
            {showLeadCaptureButton && (
                <TeamLeadCaptureOverlay
                    onClose={handleLeadCaptureButtonClose}
                    onSuccess={handleLeadCaptureButtonSuccess}
                    cardLayoutType={contextCardLayout}
                    addMoreConnectButtonColor={secondaryColor}
                    triggerSource="button"
                />
            )}
        </Box>
    );
};

export default TeamCardDesign5;